import { useCallback, useMemo, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons'
import { zodResolver } from '@hookform/resolvers/zod'

import { ButtonGroup, Card, CardBody, CardHeader } from 'Components/_theme'
import { Field } from 'Components/Field/Field'
import { FieldTypeEnum } from 'Components/Field/Field.types'

import { useStore } from 'Stores/index'

import {
  fetchFromPublicApiUsingPartialPath,
  getSelectValue,
  handleFetchErrorFunctional,
} from 'Utilities'

import {
  E2E_CONTACT_US_FORM_MESSAGE,
  E2E_CONTACT_US_FORM_REASONS,
  E2E_CONTACT_US_FORM_SUBMIT,
  E2E_CONTACT_US_FORM_SUCCESSFUL_MESSAGE,
  E2E_ERROR,
  E2E_HEADING_CONTACT,
} from 'Constants/e2e'
import { EMPTY_SELECT_OPTION } from 'Constants/form'

import { HUBSPOT_QUESTION_MODAL_REASON_OPTIONS } from './HubspotQuestionModal.constants'
import { hubspotQuestionModalFormSchema } from './HubspotQuestionModal.schema'
import {
  HubspotQuestionModalFormSchema,
  HubspotQuestionModalProps,
} from './HubspotQuestionModal.types'

export const HubspotQuestionModal = (props: HubspotQuestionModalProps) => {
  const { onClose } = props
  const { userStore } = useStore()
  const { user } = userStore
  const intl = useIntl()

  const [hasError, setHasError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccessfulSubmitted, setIsSuccessfulSubmitted] = useState(false)
  const reasonOptions = HUBSPOT_QUESTION_MODAL_REASON_OPTIONS.map(option => ({
    ...option,
    label: intl.formatMessage({ id: option.label }),
  }))

  const formMethods = useForm<HubspotQuestionModalFormSchema>({
    defaultValues: {
      email: user.email || '',
      message: '',
      name: user.fullName || '',
      reason: EMPTY_SELECT_OPTION,
    },
    resolver: zodResolver(hubspotQuestionModalFormSchema),
  })

  const handleSuccessfulSubmit = useCallback(() => {
    setHasError(false)
    setIsSubmitting(false)
    setIsSuccessfulSubmitted(true)
  }, [])

  const handleBackClick = useCallback(() => {
    setHasError(false)
    setIsSubmitting(false)
    setIsSuccessfulSubmitted(false)
  }, [])

  const handleModalClose = useCallback(() => {
    setHasError(false)
    setIsSubmitting(false)
    setIsSuccessfulSubmitted(false)

    onClose()
  }, [onClose])

  const handleSubmit = formMethods.handleSubmit(data => {
    setIsSubmitting(true)

    fetchFromPublicApiUsingPartialPath('/support/ticket', {
      body: JSON.stringify({
        email: data.email,
        fullName: data.name,
        message: data.message,
        reason: getSelectValue(data.reason),
        userId: user.userId,
      }),
      headers: {
        Authorization: `Bearer ${user.jwt}`,
      },
      method: 'POST',
    })
      .then(handleSuccessfulSubmit)
      .catch((error: Error) => {
        setHasError(true)

        handleFetchErrorFunctional()(error)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  })

  const renderSuccessSubmissionContent = () => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      data-e2e={E2E_CONTACT_US_FORM_SUCCESSFUL_MESSAGE}
      style={{ height: 500 }}
    >
      <div className="m-2 text-center text-muted">
        <FormattedMessage id="YourRequestHasBeenSubmitted" />
        <br />
        <FormattedMessage id="OurSupportWillGetInTouch" />
      </div>
    </div>
  )

  const renderErrorSubmissionContent = () => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      data-e2e={E2E_ERROR}
      style={{ height: 500 }}
    >
      <div className="m-2 text-center text-muted">
        <FormattedMessage id="ErrorSubmittingRequest" />
        <br />
        <FormattedMessage id="PleaseTryAgainLater" />
      </div>
    </div>
  )

  const renderErrorMessage = useMemo(
    () => (
      <>
        {renderErrorSubmissionContent()}

        <ButtonGroup
          leftButton={{
            onClick: handleBackClick,
            text: intl.formatMessage({ id: 'Back' }),
          }}
          rightButton={{
            onClick: handleModalClose,
            text: intl.formatMessage({ id: 'Close' }),
          }}
        />
      </>
    ),
    [handleBackClick, handleModalClose, intl],
  )

  const renderSuccessMessage = useMemo(
    () => (
      <>
        {renderSuccessSubmissionContent()}

        <ButtonGroup
          rightButton={{
            onClick: handleModalClose,
            text: intl.formatMessage({ id: 'Close' }),
          }}
        />
      </>
    ),
    [handleModalClose, intl],
  )

  const renderQuestionForm = useMemo(
    () => (
      <FormProvider {...formMethods}>
        <Form id="HUBSPOT_QUESTION_MODAL_FORM" onSubmit={handleSubmit}>
          <Field
            containerClassName="mb-2"
            label="Name"
            markAsRequired
            name="name"
            shouldTranslateLabel
            type={FieldTypeEnum.text}
          />

          <Field
            containerClassName="mb-2"
            disabled
            label="Email"
            markAsRequired
            name="email"
            shouldTranslateLabel
            type={FieldTypeEnum.text}
          />

          <Field
            containerClassName="mb-2"
            e2eSelector={E2E_CONTACT_US_FORM_REASONS}
            label="Reason"
            markAsRequired
            name="reason"
            selectProps={{
              options: reasonOptions,
            }}
            shouldTranslateLabel
            type={FieldTypeEnum.select}
          />

          <Field
            containerClassName="mb-2"
            e2eSelector={E2E_CONTACT_US_FORM_MESSAGE}
            label="Message"
            markAsRequired
            name="message"
            shouldTranslateLabel
            textareaProps={{
              maxLength: 1000,
              showCounter: true,
            }}
            type={FieldTypeEnum.textareaAutosize}
          />

          <ButtonGroup
            leftButton={{
              onClick: handleModalClose,
              text: intl.formatMessage({ id: 'Cancel' }),
            }}
            rightButton={{
              'data-e2e': E2E_CONTACT_US_FORM_SUBMIT,
              disabled: hasError || isSubmitting,
              id: 'next',
              text: intl.formatMessage({
                id: isSubmitting ? 'Loading' : 'HubspotQuestionModal_submit',
              }),
              type: 'submit',
            }}
          />
        </Form>
      </FormProvider>
    ),
    [
      formMethods,
      handleModalClose,
      handleSubmit,
      hasError,
      intl,
      isSubmitting,
      reasonOptions,
    ],
  )

  return (
    <Card>
      <CardHeader e2eSelector={E2E_HEADING_CONTACT} icon={faEnvelope}>
        <FormattedMessage id="Contact_us" />
      </CardHeader>

      <CardBody>
        {hasError && renderErrorMessage}

        {isSuccessfulSubmitted && renderSuccessMessage}

        {!hasError && !isSuccessfulSubmitted && user && renderQuestionForm}
      </CardBody>
    </Card>
  )
}
